import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { formatINR, formatNum } from "@/lib/format";
import { FileSpreadsheet, FileText, TrendingUp, AlertTriangle, CheckCircle2, Globe } from "lucide-react";
import { toast } from "sonner";

export default function Reports() {
  const { effectiveRole } = useAuth();
  const canWrite = effectiveRole !== "viewer";
  const [summary, setSummary] = useState(null);

  useEffect(() => { api.get("/reports/daily-summary").then((r) => setSummary(r.data)); }, []);

  const download = async (fmt) => {
    try {
      const res = await api.get(`/reports/export?fmt=${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = fmt === "pdf" ? "youngness-report.pdf" : "youngness-report.xlsx";
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch (e) { toast.error("Export failed"); }
  };

  const Stat = ({ icon: Icon, label, value, accent }) => (
    <Card className="p-5">
      <div className={`h-9 w-9 rounded-xl bg-secondary flex items-center justify-center ${accent} mb-3`}><Icon className="h-[18px] w-[18px]" /></div>
      <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="mt-1 font-display text-2xl font-extrabold tabular-nums">{value}</p>
    </Card>
  );

  return (
    <div data-testid="reports-page">
      <PageHeader eyebrow="Reporting" title="Executive Reports"
        subtitle="Auto-generated daily CEO summary with one-click export to Excel and PDF."
        action={
          <div className="flex gap-2" style={{ display: canWrite ? undefined : "none" }}>
            <Button variant="outline" onClick={() => download("excel")} data-testid="export-excel-button"><FileSpreadsheet className="h-4 w-4 mr-1" /> Excel</Button>
            <Button onClick={() => download("pdf")} data-testid="export-pdf-button"><FileText className="h-4 w-4 mr-1" /> PDF</Button>
          </div>
        } />

      <Card className="p-5 mb-6">
        <h3 className="font-display font-semibold mb-1">Daily CEO Summary</h3>
        <p className="text-sm text-muted-foreground mb-4">{summary?.date} · yesterday's performance at a glance</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Stat icon={TrendingUp} label="Revenue (yest.)" value={formatINR(summary?.yesterday?.revenue)} accent="text-emerald-600" />
          <Stat icon={TrendingUp} label="Leads (yest.)" value={formatNum(summary?.yesterday?.leads)} accent="text-sky-600" />
          <Stat icon={CheckCircle2} label="Procedures" value={formatNum(summary?.yesterday?.procedures)} accent="text-violet-600" />
          <Stat icon={Globe} label="Sites Down" value={`${summary?.sites_down ?? 0} / ${summary?.sites_total ?? 0}`} accent="text-rose-600" />
        </div>
      </Card>

      <div className="grid sm:grid-cols-2 gap-4">
        <Card className="p-5">
          <div className="flex items-center gap-2 mb-2"><AlertTriangle className="h-4 w-4 text-amber-500" /><h3 className="font-display font-semibold">Issues</h3></div>
          <div className="flex gap-6 mt-3">
            <div><p className="text-3xl font-extrabold text-rose-600 tabular-nums">{summary?.issues_open ?? 0}</p><p className="text-xs text-muted-foreground">Open / In Progress</p></div>
            <div><p className="text-3xl font-extrabold text-emerald-600 tabular-nums">{summary?.issues_solved ?? 0}</p><p className="text-xs text-muted-foreground">Solved</p></div>
          </div>
        </Card>
        <Card className="p-5">
          <h3 className="font-display font-semibold mb-2">Weekly & Monthly Reports</h3>
          <p className="text-sm text-muted-foreground">Per-department and per-center breakdowns are included in the exported Excel/PDF. Use the buttons above to download the full center-performance report.</p>
        </Card>
      </div>
    </div>
  );
}
